import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  path: string;
  icon?: string;
  /** Admin-only items (gated server-side by `*:*`) are hidden from non-superusers (#353). */
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Sources', path: '/sources' },
  { label: 'Datasets', path: '/datasets' },
  { label: 'Ingestion', path: '/ingestion' },
  { label: 'Connectors', path: '/connectors' },
  { label: 'Catalog', path: '/catalog' },
  { label: 'Views', path: '/views' },
  { label: 'Quality', path: '/quality' },
  { label: 'Schemas', path: '/schemas' },
  { label: 'Monitors', path: '/monitors' },
  { label: 'Lineage', path: '/lineage' },
  { label: 'Settings', path: '/settings' },
  { label: 'Users', path: '/users', adminOnly: true },
  { label: 'Roles', path: '/roles', adminOnly: true },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="flex h-16 items-center px-6">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="text-xl font-bold text-primary">Lakehouse</div>
            </Link>
            <nav className="flex gap-6">
              {navItems
                .filter((item) => !item.adminOnly || user?.is_superuser)
                .map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'text-sm font-medium transition-colors hover:text-primary',
                    location.pathname === item.path
                      ? 'text-primary'
                      : 'text-muted-foreground'
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              {user?.username}
              {user?.is_superuser && (
                <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                  Admin
                </span>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">{children}</main>
    </div>
  );
};
